/**
 * @fileoverview OrdenProduccionRepository - Repositorio especializado para órdenes de producción
 */

import { DataRepository } from './base/DataRepository';
import type { 
  OrdenProduccion, 
  OrdenProduccionExpanded,
  CreateOrdenProduccionData, 
  UpdateOrdenProduccionData 
} from '../types/collections/OrdenProduccion';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from '../types/common/PocketBaseTypes';
import { createModuleLogger } from '../utils/logger';

/**
 * Interface para filtros de reporte de órdenes
 */
export interface ReporteFilters {
  estados: {
    finalizada: boolean;
    enProceso: boolean;
    enEdicion: boolean;
  };
  fechaCreacion: { inicio: string | null; fin: string | null };
  fechaInicio: { inicio: string | null; fin: string | null };
  fechaFinalizacion: { inicio: string | null; fin: string | null };
  fechaEntrega: { inicio: string | null; fin: string | null };
  trabajoInterno: 'si' | 'no' | 'todos';
  cliente: string | null;
  producto: string | null;
  soloReprocesos?: boolean;
}

/**
 * Repositorio especializado para la gestión de órdenes de producción
 * Extiende DataRepository con métodos específicos del dominio
 */
export class OrdenProduccionRepository extends DataRepository<OrdenProduccion> {
  private finalizarLogger = createModuleLogger('OrdenProduccionRepository');
  
  constructor() {
    super('orden_produccion');
  }

  /**
   * Obtiene órdenes activas (en producción) no finalizadas
   * Filtra órdenes con campo activa asignado pero NO finalizadas
   */
  async getActiveOrders(page: number = 1, pageSize: number = 10): Promise<ListResult<OrdenProduccion>> {
    try {
      // Filtrar órdenes que tengan campo activa asignado pero NO estén finalizadas
      // Esto incluye órdenes que están en producción
      const result = await this.pb.collection(this.collectionName).getList<OrdenProduccion>(page, pageSize, {
        filter: 'activa != "" && finalizada = ""', // Órdenes activas pero no finalizadas
        sort: '-created', // Más recientes primero
        expand: 'cliente,producto,asesor_comercial,material,medida'
      });
      
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes activas: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ACTIVE_ORDERS_ERROR',
        error
      );
    }
  }

  /**
   * Busca una orden por su número
   * Equivale a: pb.collection("orden_produccion").getFirstListItem(`numero=${numero}`)
   */
  async getByNumero(numero: number): Promise<OrdenProduccion | null> {
    try {
      const orden = await this.pb.collection(this.collectionName)
        .getFirstListItem<OrdenProduccion>(`numero=${numero}`);
      return orden;
    } catch (error) {
      // Si no se encuentra, retornamos null en lugar de lanzar error
      if ((error instanceof Error && error.message?.includes('Not found')) || 
          (error as any)?.status === 404) {
        return null;
      }
      throw new RepositoryError(
        `Error al buscar orden por número ${numero}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_NUMERO_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene órdenes de un cliente específico
   * Filtradas por cliente activo
   */
  async getByCliente(clienteId: string, options: QueryOptions = {}): Promise<OrdenProduccion[]> {
    try {
      const result = await this.pb.collection(this.collectionName).getList<OrdenProduccion>(
        1, 
        50, // Límite razonable para órdenes de un cliente
        {
          ...options,
          filter: `cliente="${clienteId}" && activo=true`,
          sort: '-numero' // Ordenar por número descendente
        }
      );
      return result.items;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes del cliente ${clienteId}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_CLIENTE_ERROR',
        error
      );
    }
  }

  /**
   * Desactiva una orden (soft delete)
   * Equivale a: pb.collection("orden_produccion").update(id, { activo: false })
   */
  async deactivate(id: string): Promise<boolean> {
    try {
      await this.pb.collection(this.collectionName).update<OrdenProduccion>(id, { 
        activo: false 
      });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al desactivar orden ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'DEACTIVATE_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene una orden con todas sus relaciones expandidas
   * Incluye: cliente, asesor_comercial, tipo_papel, planeacion (con tipo_pinza y tamano), 
   * solicitud, producto, corte_conversion (con papel, parametro e impresion anidadas),
   * brillo_interno, pegue_interno, plastificado_interno, troquelado_interno, 
   * acabado_interno, recubrimiento
   */
  async getWithAllRelations(id: string): Promise<OrdenProduccionExpanded> {
    try {
      const expandFields = [
        'cliente',
        'asesor_comercial', 
        'tipo_papel',
        'planeacion',
        'planeacion.tipo_pinza',
        'planeacion.tamano',
        'planeacion.parametros_tinta',
        'planeacion.parametros_tinta.tinta',
        'solicitud',
        'producto',
        'corte_conversion',
        'corte_conversion.papel',
        'corte_conversion.parametro',
        'corte_conversion.parametro.impresion',
        'corte_conversion.parametro.impresion.parametros_tinta',
        'corte_conversion.parametro.impresion.parametros_tinta.tinta',
        'brillo_interno',
        'pegue_interno', 
        'plastificado_interno',
        'troquelado_interno',
        'acabado_interno',
        'recubrimiento'
      ].join(',');

      return await this.pb.collection(this.collectionName)
        .getOne<OrdenProduccionExpanded>(id, {
          expand: expandFields
        });
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener orden ${id} con relaciones: ${error instanceof Error ? error.message : String(error)}`,
        'GET_WITH_RELATIONS_ERROR',
        error
      );
    }
  }

  /**
   * Crea una nueva orden con validaciones de negocio
   * Equivale a: pb.collection("orden_produccion").create(orderData)
   */
  async createWithValidation(orderData: CreateOrdenProduccionData): Promise<OrdenProduccion> {
    try {
      // Validaciones de negocio antes de crear
      if (!orderData.cliente) {
        throw new Error('El cliente es requerido');
      }

      // Asegurar que la orden se crea como activa por defecto
      const dataToCreate: CreateOrdenProduccionData = {
        ...orderData,
        // asegurar envío explícito del nuevo campo
        numero_reproceso: (orderData as any).numero_reproceso,
        activo: (orderData as any).activo !== undefined ? (orderData as any).activo as any : (true as any)
      } as CreateOrdenProduccionData;

      return await this.pb.collection(this.collectionName).create<OrdenProduccion>(dataToCreate);
    } catch (error) {
      throw new RepositoryError(
        `Error al crear orden: ${error instanceof Error ? error.message : String(error)}`,
        'CREATE_WITH_VALIDATION_ERROR',
        error
      );
    }
  }

  /**
   * Actualiza una orden con validaciones de negocio
   * Equivale a: pb.collection("orden_produccion").update(id, orderData)
   */
  async updateWithValidation(id: string, orderData: UpdateOrdenProduccionData): Promise<OrdenProduccion> {
    try {
      // Obtener orden actual para validaciones
      const currentOrder = await this.pb.collection(this.collectionName)
        .getOne<OrdenProduccion>(id);

      if (!currentOrder) {
        throw new Error(`Orden ${id} no encontrada`);
      }

      // Validaciones de negocio
      if (orderData.activo === false && currentOrder.activo === false) {
        throw new Error('La orden ya está desactivada');
      }

      const dataToUpdate: UpdateOrdenProduccionData = {
        ...orderData,
        // asegurar envío explícito del nuevo campo
        numero_reproceso: (orderData as any).numero_reproceso,
      };
      return await this.pb.collection(this.collectionName).update<OrdenProduccion>(id, dataToUpdate);
    } catch (error) {
      throw new RepositoryError(
        `Error al actualizar orden ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'UPDATE_WITH_VALIDATION_ERROR',
        error
      );
    }
  }

  /**
   * Busca órdenes por criterios múltiples
   * Útil para filtros avanzados en la interfaz
   */
  async search(criteria: {
    numero?: number;
    cliente?: string;
    fechaInicio?: string;
    fechaEntrega?: string;
    activo?: boolean;
  }, page: number = 1, pageSize: number = 10): Promise<ListResult<OrdenProduccion>> {
    try {
      const filters: string[] = [];

      if (criteria.numero) {
        filters.push(`numero=${criteria.numero}`);
      }
      if (criteria.cliente) {
        filters.push(`cliente="${criteria.cliente}"`);
      }
      if (criteria.fechaInicio) {
        filters.push(`fecha_inicio>="${criteria.fechaInicio}"`);
      }
      if (criteria.fechaEntrega) {
        filters.push(`fecha_entrega<="${criteria.fechaEntrega}"`);
      }
      if (criteria.activo !== undefined) {
        filters.push(`activo=${criteria.activo}`);
      }

      const filter = filters.length > 0 ? filters.join(' && ') : '';

      return await this.pb.collection(this.collectionName).getList<OrdenProduccion>(
        page, 
        pageSize, 
        { filter }
      );
    } catch (error) {
      throw new RepositoryError(
        `Error en búsqueda de órdenes: ${error instanceof Error ? error.message : String(error)}`,
        'SEARCH_ERROR',
        error
      );
    }
  }

  /**
   * Carga una orden con datos relacionados pre-cargados para formularios
   * Útil para reducir consultas al abrir modales de edición
   */
  async getWithFormData(id: string): Promise<{
    orden: OrdenProduccionExpanded;
    formData: {
      clientes: any[];
      usuarios: any[];
      materiales: any[];
      productos: any[];
      tiposSolicitud: any[];
    };
  }> {
    try {
      // Importación dinámica para evitar dependencias circulares
      const { RelatedDataHelper } = await import('./helpers/RelatedDataHelper');

      // Cargar orden y datos del formulario en paralelo
      const [orden, formData] = await Promise.all([
        this.getWithAllRelations(id),
        RelatedDataHelper.getAllFormData()
      ]);

      return {
        orden,
        formData: {
          clientes: formData.clientes,
          usuarios: formData.usuarios,
          materiales: formData.materiales,
          productos: formData.productos,
          tiposSolicitud: formData.tiposSolicitud
        }
      };
    } catch (error) {
      throw new RepositoryError(
        `Error al cargar orden ${id} con datos de formulario: ${error instanceof Error ? error.message : String(error)}`,
        'GET_WITH_FORM_DATA_ERROR',
        error
      );
    }
  }

  /**
   * Carga múltiples órdenes con expansiones optimizadas
   * Útil para listas con información relacionada
   */
  async getListWithRelations(
    page: number = 1, 
    pageSize: number = 10,
    relations: string[] = ['cliente', 'asesor_comercial']
  ): Promise<ListResult<OrdenProduccionExpanded>> {
    try {
      const expandFields = relations.join(',');
      
      return await this.pb.collection(this.collectionName).getList<OrdenProduccionExpanded>(
        page, 
        pageSize, 
        {
          filter: 'activo=true',
          sort: '-numero', // Cambiar a ordenar por número descendente para consistencia
          expand: expandFields
        }
      );
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener lista con relaciones: ${error instanceof Error ? error.message : String(error)}`,
        'GET_LIST_WITH_RELATIONS_ERROR',
        error
      );
    }
  }

  /**
   * Búsqueda optimizada con filtros relacionados
   * Permite buscar por campos de entidades relacionadas
   */
  async searchWithRelatedFilters(criteria: {
    numero?: number;
    clienteNombre?: string;
    asesorNombre?: string;
    fechaInicio?: string;
    fechaEntrega?: string;
    activo?: boolean;
  }, page: number = 1, pageSize: number = 10): Promise<ListResult<OrdenProduccionExpanded>> {
    try {
      const filters: string[] = [];

      if (criteria.numero) {
        filters.push(`numero=${criteria.numero}`);
      }
      if (criteria.clienteNombre) {
        filters.push(`cliente.razon_social~"${criteria.clienteNombre}"`);
      }
      if (criteria.asesorNombre) {
        filters.push(`asesor_comercial.name~"${criteria.asesorNombre}"`);
      }
      if (criteria.fechaInicio) {
        filters.push(`fecha_inicio>="${criteria.fechaInicio}"`);
      }
      if (criteria.fechaEntrega) {
        filters.push(`fecha_entrega<="${criteria.fechaEntrega}"`);
      }
      if (criteria.activo !== undefined) {
        filters.push(`activo=${criteria.activo}`);
      }

      const filter = filters.length > 0 ? filters.join(' && ') : 'activo=true';

      return await this.pb.collection(this.collectionName).getList<OrdenProduccionExpanded>(
        page, 
        pageSize, 
        { 
          filter,
          expand: 'cliente,asesor_comercial,producto',
          sort: '-numero' // Cambiar a ordenar por número descendente para consistencia
        }
      );
    } catch (error) {
      throw new RepositoryError(
        `Error en búsqueda con filtros relacionados: ${error instanceof Error ? error.message : String(error)}`,
        'SEARCH_WITH_RELATED_FILTERS_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene estadísticas básicas de órdenes
   * Útil para dashboards y reportes
   */
  async getStats(): Promise<{
    totalActivas: number;
    totalInactivas: number;
    ordenesHoy: number;
  }> {
    try {
      const today = new Date().toISOString().split('T')[0];

      const [activas, inactivas, hoy] = await Promise.all([
        this.pb.collection(this.collectionName).getList<OrdenProduccion>(1, 1, {
          filter: 'activo=true'
        }),
        this.pb.collection(this.collectionName).getList<OrdenProduccion>(1, 1, {
          filter: 'activo=false'
        }),
        this.pb.collection(this.collectionName).getList<OrdenProduccion>(1, 1, {
          filter: `created>="${today}" && activo=true`
        })
      ]);

      return {
        totalActivas: activas.totalItems,
        totalInactivas: inactivas.totalItems,
        ordenesHoy: hoy.totalItems
      };
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener estadísticas: ${error instanceof Error ? error.message : String(error)}`,
        'GET_STATS_ERROR',
        error
      );
    }
  }

  /**
   * Activa una orden de producción creando un LogEstado y actualizando la orden
   * @param ordenId ID de la orden a activar
   * @param usuarioId ID del usuario que activa la orden
   * @returns Promise<{ logEstadoId: string; ordenActualizada: OrdenProduccion }>
   */
  async activarOrden(ordenId: string, usuarioId: string): Promise<{ logEstadoId: string; ordenActualizada: OrdenProduccion }> {
    try {
      // Validar que la orden no esté ya activa
      const ordenActual = await this.pb.collection(this.collectionName).getOne<OrdenProduccion>(ordenId);
      if (ordenActual.activa) {
        throw new RepositoryError(
          `La orden ${ordenId} ya está activa`,
          'ORDEN_YA_ACTIVA_ERROR',
          null
        );
      }

      // Crear registro LogEstado
      const logEstadoData = {
        usuario: usuarioId,
        fecha_estado: new Date().toISOString()
      };

      let logEstado;
      try {
        logEstado = await this.pb.collection('log_estado').create(logEstadoData);
      } catch (logError) {
        throw new RepositoryError(
          `Error al crear LogEstado: ${logError instanceof Error ? logError.message : String(logError)}`,
          'CREAR_LOG_ESTADO_ERROR',
          logError
        );
      }
      
      // Actualizar la orden con el campo activa
      let ordenActualizada;
      try {
        ordenActualizada = await this.pb.collection(this.collectionName).update(ordenId, {
          activa: logEstado.id
        });
      } catch (updateError) {
        // Si falla la actualización, intentar eliminar el LogEstado creado
        try {
          await this.pb.collection('log_estado').delete(logEstado.id);
        } catch (deleteError) {
          // Log del error de limpieza pero no lanzar excepción
          console.error('Error al limpiar LogEstado después de fallo:', deleteError);
        }
        
        throw new RepositoryError(
          `Error al actualizar orden: ${updateError instanceof Error ? updateError.message : String(updateError)}`,
          'ACTUALIZAR_ORDEN_ERROR',
          updateError
        );
      }

      // Completar reservas y actualizar inventario
      try {
        // Obtener todas las reservas activas de esta orden
        const reservas = await this.pb.collection('reserva_inventario').getList(1, 1000, {
          filter: `orden_produccion = "${ordenId}" && completado = false && activo = true`,
          expand: 'producto_inventario'
        });

        console.log(`[OrdenProduccionRepository] Completando ${reservas.items.length} reservas para orden ${ordenId}`);

        // Para cada reserva, marcarla como completada y actualizar el inventario
        for (const reserva of reservas.items) {
          const cantidadReservada = reserva.total || 0;
          const productoInventarioId = reserva.producto_inventario;

          if (cantidadReservada > 0 && productoInventarioId) {
            // Obtener el inventario actual
            const inventario = await this.pb.collection('producto_inventario').getOne(productoInventarioId);
            
            // Calcular nuevos valores
            const nuevoTotal = Math.max(0, (inventario.total || 0) - cantidadReservada);
            const nuevoReservado = Math.max(0, (inventario.reservado || 0) - cantidadReservada);
            
            // Remover el ID de la reserva del array reserva
            const reservasArray = (inventario.reserva || []) as string[];
            const nuevoReservasArray = reservasArray.filter(id => id !== reserva.id);

            // Actualizar inventario: decrementar total y reservado, y remover reserva del array
            await this.pb.collection('producto_inventario').update(productoInventarioId, {
              total: nuevoTotal,
              reservado: nuevoReservado,
              reserva: nuevoReservasArray
            });

            console.log(`[OrdenProduccionRepository] Inventario actualizado: ${productoInventarioId}, Total: ${inventario.total} -> ${nuevoTotal}, Reservado: ${inventario.reservado} -> ${nuevoReservado}, Reservas: ${reservasArray.length} -> ${nuevoReservasArray.length}`);
          }

          // Marcar reserva como completada
          await this.pb.collection('reserva_inventario').update(reserva.id, {
            completado: true
          });

          console.log(`[OrdenProduccionRepository] Reserva ${reserva.id} marcada como completada`);
        }
      } catch (reservaError) {
        // Log el error pero no fallar la activación de la orden
        console.error('[OrdenProduccionRepository] Error al completar reservas:', reservaError);
        // No lanzar error para no bloquear la activación de la orden
      }

      return {
        logEstadoId: logEstado.id,
        ordenActualizada: ordenActualizada as OrdenProduccion
      };
    } catch (error) {
      // Si ya es un RepositoryError, relanzarlo
      if (error instanceof RepositoryError) {
        throw error;
      }
      
      throw new RepositoryError(
        `Error al activar orden: ${error instanceof Error ? error.message : String(error)}`,
        'ACTIVAR_ORDEN_ERROR',
        error
      );
    }
  }

  /**
   * Verifica si una orden está activa
   * @param ordenId ID de la orden a verificar
   * @returns Promise<boolean>
   */
  async isOrdenActiva(ordenId: string): Promise<boolean> {
    try {
      const orden = await this.pb.collection(this.collectionName).getOne<OrdenProduccion>(ordenId);
      return !!orden.activa;
    } catch (error) {
      throw new RepositoryError(
        `Error al verificar estado de orden: ${error instanceof Error ? error.message : String(error)}`,
        'CHECK_ORDEN_STATUS_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene órdenes para edición (no activas)
   * @param page Número de página
   * @param pageSize Tamaño de página
   * @returns Promise<ListResult<OrdenProduccion>>
   */
  async getOrdersForEditing(page: number = 1, pageSize: number = 10): Promise<ListResult<OrdenProduccion>> {
    try {
      // Filtrar órdenes que estén activas (activo=true) pero NO tengan campo activa asignado
      // Esto excluye órdenes que ya están en producción
      const result = await this.pb.collection(this.collectionName).getList<OrdenProduccion>(page, pageSize, {
        filter: 'activo=true && activa = ""', // Órdenes activas pero sin campo activa (para edición)
        sort: '-numero'
      });
      
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes para edición: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ORDERS_FOR_EDITING_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene órdenes en producción (con campo activa asignado) no finalizadas
   * 
   * DIFERENCIA IMPORTANTE:
   * - getActiveOrders(): Filtra por activo=true (campo booleano que indica si la orden está habilitada en el sistema)
   * - getOrdersInProduction(): Filtra por activa!="" (campo de relación que indica que la orden está en proceso de producción)
   * 
   * @param page Número de página
   * @param pageSize Tamaño de página
   * @returns Promise<ListResult<OrdenProduccion>>
   */
  async getOrdersInProduction(page: number = 1, pageSize: number = 10, options: QueryOptions = {}): Promise<ListResult<OrdenProduccion>> {
    try {
      // Filtrar órdenes que tengan campo activa asignado pero NO estén finalizadas
      // Esto incluye órdenes que están en proceso de producción
      const result = await this.pb.collection(this.collectionName).getList<OrdenProduccion>(page, pageSize, {
        filter: 'activa != "" && finalizada = ""', // Órdenes en producción pero no finalizadas
        sort: '-created', // Más recientes primero
        expand: 'cliente,asesor_comercial,solicitud,producto,planeacion,corte_conversion,impresion,recubrimiento,material,medida,tarea,activa',
        ...options // Merge con opciones adicionales como requestKey
      });
      
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes en producción: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ORDERS_IN_PRODUCTION_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene órdenes en producción que NO tienen tarea asignada
   * Útil para mostrar órdenes disponibles para asignar a un plan
   *
   * @param page Número de página
   * @param pageSize Tamaño de página
   * @param options Opciones adicionales de consulta
   * @returns Promise<ListResult<OrdenProduccion>>
   */
  async getOrdersInProductionWithoutTask(page: number = 1, pageSize: number = 10, options: QueryOptions = {}): Promise<ListResult<OrdenProduccion>> {
    try {
      // Primero obtener IDs de órdenes que tienen tarea asignada
      const tareasResult = await this.pb.collection('tarea_orden').getList(1, 1000, {
        fields: 'orden_produccion',
        requestKey: null
      });

      const ordenesConTareaIds = tareasResult.items.map(tarea => tarea.orden_produccion);

      // Construir filtro para excluir órdenes con tarea
      let filter = 'activa != "" && finalizada = ""';

      if (ordenesConTareaIds.length > 0) {
        // Agregar condición para excluir IDs que tienen tarea
        const excludeFilter = ordenesConTareaIds.map(id => `id != "${id}"`).join(' && ');
        filter = `(${filter}) && (${excludeFilter})`;
      }

      // Obtener órdenes sin tarea
      const result = await this.pb.collection(this.collectionName).getList<OrdenProduccion>(page, pageSize, {
        filter,
        sort: '-created',
        expand: 'cliente,asesor_comercial,solicitud,producto,planeacion,corte_conversion,impresion,recubrimiento,material,medida,tarea,activa',
        ...options
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes en producción sin tarea: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ORDERS_WITHOUT_TASK_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene órdenes finalizadas que además estuvieron activas (ambos campos no vacíos)
   * Filtro: activa != "" && finalizada != ""
   */
  async getOrdersFinalizadas(page: number = 1, pageSize: number = 10, options: QueryOptions = {}): Promise<ListResult<OrdenProduccion>> {
    try {
      // Filtrar órdenes que tengan activa y finalizada con valores no vacíos
      const result = await this.pb.collection(this.collectionName).getList<OrdenProduccion>(page, pageSize, {
        filter: 'activa != "" && finalizada != ""',
        sort: '-created',
        expand: 'cliente,asesor_comercial,solicitud,producto,planeacion,corte_conversion,impresion,recubrimiento,material,medida,tarea',
        ...options // Merge con opciones adicionales como requestKey
      });

      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes finalizadas: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ORDERS_FINALIZADAS_ERROR',
        error
      );
    }
  }

  /**
   * Elimina un registro de LogEstado
   * 
   * @param logEstadoId ID del registro de LogEstado a eliminar
   * @returns Promise<boolean> True si se eliminó exitosamente
   */
  async deleteLogEstado(logEstadoId: string): Promise<boolean> {
    try {
      await this.pb.collection('log_estado').delete(logEstadoId);
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al eliminar LogEstado ${logEstadoId}: ${error instanceof Error ? error.message : String(error)}`,
        'DELETE_LOG_ESTADO_ERROR',
        error
      );
    }
  }

  /**
   * Finaliza una orden de producción creando LogEstado y actualizando campo finalizada
   * 
   * @param ordenId ID de la orden a finalizar
   * @param usuarioId ID del usuario que finaliza la orden
   * @returns Promise<boolean> True si se finalizó exitosamente
   */
  async finalizarOrden(ordenId: string, usuarioId: string): Promise<boolean> {
    try {
      // Verificar que la orden existe y no esté ya finalizada
      const orden = await this.pb.collection(this.collectionName).getOne<OrdenProduccion>(ordenId);
      
      if (!orden) {
        throw new Error(`Orden con ID ${ordenId} no encontrada`);
      }
      
      if (orden.finalizada && orden.finalizada !== "") {
        throw new Error(`La orden ${orden.numero} ya está finalizada`);
      }

      // Crear registro LogEstado para la finalización
      const logEstadoData = {
        orden_produccion: ordenId,
        usuario: usuarioId,
        accion: 'finalizar_orden',
        descripcion: `Orden ${orden.numero} finalizada por usuario ${usuarioId}`,
        fecha: new Date().toISOString(),
        estado_anterior: orden.estado || 'En producción',
        estado_nuevo: 'Finalizada'
      };

      let logEstadoId: string;
      
      try {
        // Crear LogEstado
        const logEstado = await this.pb.collection('log_estado').create(logEstadoData);
        logEstadoId = logEstado.id;
        
        this.finalizarLogger.info('LogEstado creado exitosamente', { 
          logEstadoId, 
          ordenId, 
          usuarioId 
        });
        
      } catch (logError) {
        throw new RepositoryError(
          `Error al crear LogEstado: ${logError instanceof Error ? logError.message : String(logError)}`,
          'CREATE_LOG_ESTADO_ERROR',
          logError
        );
      }

      try {
        // Actualizar campo finalizada de la orden
        await this.pb.collection(this.collectionName).update(ordenId, {
          finalizada: logEstadoId
        });
        
        this.finalizarLogger.info('Campo finalizada actualizado exitosamente', { 
          ordenId, 
          logEstadoId 
        });
        
      } catch (updateError) {
        // Si falla la actualización, intentar eliminar el LogEstado creado
        try {
          await this.pb.collection('log_estado').delete(logEstadoId);
          this.finalizarLogger.warn('LogEstado eliminado por rollback', { logEstadoId });
        } catch (deleteError) {
          this.finalizarLogger.error('Error al eliminar LogEstado durante rollback', { 
            logEstadoId, 
            error: deleteError instanceof Error ? deleteError.message : String(deleteError) 
          });
        }
        
        throw new RepositoryError(
          `Error al actualizar campo finalizada: ${updateError instanceof Error ? updateError.message : String(updateError)}`,
          'UPDATE_FINALIZADA_ERROR',
          updateError
        );
      }

      return true;
      
    } catch (error) {
      throw new RepositoryError(
        `Error al finalizar orden: ${error instanceof Error ? error.message : String(error)}`,
        'FINALIZAR_ORDEN_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene órdenes de producción filtradas para generar reporte en Excel
   * Encapsula toda la lógica de consulta a PocketBase con múltiples filtros
   */
  async getOrdenesParaReporte(filters: ReporteFilters): Promise<OrdenProduccionExpanded[]> {
    try {
      const filterParts: string[] = [];

      // Estado
      // Lógica de estados:
      // - En Edición: activa = null AND finalizada = null
      // - En Proceso: activa != null AND finalizada = null
      // - Finalizada: activa != null AND finalizada != null
      if (filters.estados.finalizada || filters.estados.enProceso || filters.estados.enEdicion) {
        const estadoFilters: string[] = [];
        if (filters.estados.finalizada) {
          estadoFilters.push('(activa != null && finalizada != null)');
        }
        if (filters.estados.enProceso) {
          estadoFilters.push('(activa != null && finalizada = null)');
        }
        if (filters.estados.enEdicion) {
          estadoFilters.push('(activa = null && finalizada = null)');
        }
        if (estadoFilters.length > 0) {
          filterParts.push(`(${estadoFilters.join(' || ')})`);
        }
      }

      // Fechas de creación
      if (filters.fechaCreacion.inicio && filters.fechaCreacion.fin) {
        filterParts.push(`created >= "${filters.fechaCreacion.inicio}" && created <= "${filters.fechaCreacion.fin}"`);
      }

      // Fechas de inicio
      if (filters.fechaInicio.inicio && filters.fechaInicio.fin) {
        filterParts.push(`fecha_inicio >= "${filters.fechaInicio.inicio}" && fecha_inicio <= "${filters.fechaInicio.fin}"`);
      }

      // Fechas de finalización
      // Nota: PocketBase no permite filtrar directamente campos de relaciones expandidas
      // Se debe hacer filtro post-procesamiento en el cliente comparando finalizada.created
      // Por ahora, solo se filtra que exista finalizada, el filtro por fecha se hace en el cliente
      if (filters.fechaFinalizacion.inicio && filters.fechaFinalizacion.fin) {
        filterParts.push('finalizada != null');
      }

      // Fechas de entrega
      if (filters.fechaEntrega.inicio && filters.fechaEntrega.fin) {
        filterParts.push(`fecha_entrega >= "${filters.fechaEntrega.inicio}" && fecha_entrega <= "${filters.fechaEntrega.fin}"`);
      }

      // Trabajo interno
      if (filters.trabajoInterno === 'si') {
        filterParts.push('trabajo_interno = true');
      } else if (filters.trabajoInterno === 'no') {
        filterParts.push('trabajo_interno = false');
      }

      // Cliente
      if (filters.cliente) {
        filterParts.push(`cliente = "${filters.cliente}"`);
      }

      // Producto
      if (filters.producto) {
        filterParts.push(`producto = "${filters.producto}"`);
      }

      // Solo reprocesos (numero_reproceso no vacío y mayor a 0)
      if (filters.soloReprocesos) {
        filterParts.push('numero_reproceso != "" && numero_reproceso > 0');
      }

      const filter = filterParts.join(' && ');

      // Obtener órdenes con expansión de cliente, producto, activa y finalizada
      // Nota: No usamos 'fields' para asegurar que las relaciones se expandan correctamente
      // Los campos expandidos (cliente, producto, activa, finalizada) se obtienen completos cuando se expanden
      let ordenes = await this.pb.collection(this.collectionName).getFullList<OrdenProduccionExpanded>({
        filter,
        expand: 'cliente,producto,activa,finalizada',
        sort: 'numero',
        // fields: removido para asegurar que cliente, producto, activa y finalizada se expandan completamente
      });

      // Filtro post-procesamiento para fecha de finalización
      // (PocketBase no permite filtrar directamente campos de relaciones expandidas)
      // Usamos fecha_estado del log_estado en lugar de created
      if (filters.fechaFinalizacion.inicio && filters.fechaFinalizacion.fin) {
        const inicio = new Date(filters.fechaFinalizacion.inicio);
        const fin = new Date(filters.fechaFinalizacion.fin);
        fin.setHours(23, 59, 59, 999); // Incluir todo el día de fin

        ordenes = ordenes.filter(orden => {
          if (!orden.expand?.finalizada?.fecha_estado) return false;
          const fechaFinalizacion = new Date(orden.expand.finalizada.fecha_estado);
          return fechaFinalizacion >= inicio && fechaFinalizacion <= fin;
        });
      }

      return ordenes;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes para reporte: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ORDENES_REPORTE_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene lista de órdenes activas para autocompletado
   * Retorna solo id y numero ordenados por número
   */
  async getOrdenesParaAutocompletado(): Promise<{ id: string; numero: number }[]> {
    try {
      const ordenes = await this.pb.collection(this.collectionName).getFullList<OrdenProduccion>({
        filter: 'activo = true',
        sort: 'numero',
        fields: 'id,numero'
      });
      // Filtrar órdenes que no tengan número y mapear al formato requerido
      return ordenes
        .filter(orden => orden.numero !== undefined && orden.numero !== null)
        .map(orden => ({ id: orden.id, numero: orden.numero! }));
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener órdenes para autocompletado: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ORDENES_AUTOCOMPLETE_ERROR',
        error
      );
    }
  }
}
